/**
 * Detail Header
 *
 * Shared header component for detail pages with title, status, and actions.
 * Supports custom buttons configured in Object Manager.
 */

import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { StandardButton } from '../standard';
import { HeaderButtons } from '../common/CustomButtons';
import { ModalType } from '../../services/queries/buttonActionQueries';

interface DetailHeaderProps {
  title: string;
  subtitle?: string;
  recordId?: string;
  status?: string;
  statusColor?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Object API name for custom buttons (e.g., 'Client__c') */
  objectName?: string;
  /** Record data for visibility rules and action execution */
  recordData?: Record<string, any>;
  /** Callback when custom button updates fields */
  onUpdateFields?: (updates: Record<string, any>) => Promise<void>;
  /** Callback when custom button opens a modal (e.g., send-email, log-call) */
  onOpenModal?: (modalType: ModalType, recordData: Record<string, any>) => void;
  /** Additional action buttons to render before Edit/Delete */
  additionalActions?: React.ReactNode;
}

export const DetailHeader: React.FC<DetailHeaderProps> = ({
  title,
  subtitle,
  recordId,
  status,
  statusColor,
  onEdit,
  onDelete,
  objectName,
  recordData,
  onUpdateFields,
  onOpenModal,
  additionalActions
}) => {
  return (
    <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {title}
            </h1>
            {status && (
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusColor}`}>
                {status}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {subtitle}
            </p>
          )}
          {recordId && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 font-mono">
              ID: {recordId}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Custom Buttons from Object Manager */}
          {objectName && recordId && recordData && (
            <HeaderButtons
              objectName={objectName}
              recordData={recordData}
              recordId={recordId}
              onUpdateFields={onUpdateFields}
              onOpenModal={onOpenModal}
              className="mr-2"
            />
          )}

          {/* Additional Actions */}
          {additionalActions}

          {onEdit != null && (
            <StandardButton
              variant="secondary"
              size="sm"
              leftIcon={<Edit className="w-4 h-4" />}
              onClick={onEdit}
            >
              Edit
            </StandardButton>
          )}
          {onDelete != null && (
            <StandardButton
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 className="w-4 h-4" />}
              onClick={onDelete}
            >
              Delete
            </StandardButton>
          )}
        </div>
      </div>
    </div>
  );
};
